import React, { createContext, useContext } from "react";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { launchDarklyClientSideID } from "../components/app.config.ts";

const LaunchDarklyTestingDefaultsContext = createContext<Record<string, any>>(
  {}
);

export function TestFlagsProvider({
  testFlags,
  children,
}: {
  testFlags: Record<string, any>;
  children: React.ReactNode;
}) {
  return (
    <LaunchDarklyTestingDefaultsContext.Provider value={testFlags}>
      {children}
    </LaunchDarklyTestingDefaultsContext.Provider>
  );
}

export function useTestingFlags() {
  return useContext(LaunchDarklyTestingDefaultsContext);
}

export async function createLaunchDarklyProvider(
  testingFlagValues: Record<string, any> = {}
) {
  return asyncWithLDProvider({
    clientSideID: launchDarklyClientSideID,
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    // 1) Prevent app-start hangs during LD outage:
    timeout: 3, // in seconds,  https://launchdarkly.com/docs/tutorials/sdk-resilience-best-practices

    // 2) Make cached flags usable when LD is unreachable:
    options: {
      bootstrap: "localStorage", //  https://launchdarkly.github.io/js-client-sdk/interfaces/LDOptions.html
      // These are useful to simulate outage by overriding endpoints
      // baseUrl: 'https://ld-blackhole.invalid',
      // streamUrl: 'https://ld-blackhole.invalid',
      // eventsUrl: 'https://ld-blackhole.invalid',
    },
  })
    .then(function constructCompoundComponent(LaunchDarklyProvider) {
      return function CompoundComponent({
        children,
      }: {
        children: React.ReactNode;
      }) {
        return (
          <LaunchDarklyProvider>
            <TestFlagsProvider testFlags={testingFlagValues}>
              {children}
            </TestFlagsProvider>
          </LaunchDarklyProvider>
        );
      };
    })
    .catch((error) => {
      console.error("Failed to initialize LaunchDarkly:", error);
      return function FallbackComponent({
        children,
      }: {
        children: React.ReactNode;
      }) {
        return (
          <TestFlagsProvider testFlags={testingFlagValues}>
            {children}
          </TestFlagsProvider>
        );
      };
    });
}
